import { Select as MuiSelect } from '@mui/material';
import { componentSizes } from '../../theme/componentSizes';
import { r, s } from '../../theme/tokens';
import { FormField } from './FormField';
import type { SelectProps as MuiSelectProps } from '@mui/material/Select';
import type { ReactElement, ReactNode } from 'react';

export type SelectProps<TValue = unknown> = Omit<MuiSelectProps<TValue>, 'label'> & {
  label?: ReactNode;
  helperText?: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
};

export const Select = <TValue,>({
  label,
  helperText,
  helpText,
  errorText,
  fullWidth = true,
  size = 'small',
  sx,
  error,
  required,
  ...rest
}: SelectProps<TValue>): ReactElement => {
  const resolvedHelpText = helpText ?? (error ? undefined : helperText);
  const resolvedErrorText = errorText ?? (error ? helperText : undefined);
  const mergedSx = [
    {
      '&.MuiInputBase-root, & .MuiInputBase-root': {
        backgroundColor: 'background.paper',
        minHeight: `${componentSizes.auth.inputHeight}px !important`,
        height: `${componentSizes.auth.inputHeight}px !important`,
        maxHeight: `${componentSizes.auth.inputHeight}px !important`,
        boxSizing: 'border-box',
      },
      '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
        backgroundColor: 'background.paper',
        borderRadius: r('md'),
        minHeight: `${componentSizes.auth.inputHeight}px !important`,
        height: `${componentSizes.auth.inputHeight}px !important`,
      },
      '& .MuiOutlinedInput-input': {
        height: '100%',
        boxSizing: 'border-box',
        paddingTop: s('none'),
        paddingBottom: s('none'),
        paddingLeft: s('md'),
        paddingRight: s('md'),
      },
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        minHeight: `${componentSizes.auth.inputHeight}px !important`,
        height: `${componentSizes.auth.inputHeight}px !important`,
        boxSizing: 'border-box',
        paddingTop: s('none'),
        paddingBottom: s('none'),
      },
      '& .MuiOutlinedInput-notchedOutline': { borderRadius: r('md') },
    },
    sx,
  ].filter(Boolean) as MuiSelectProps<TValue>['sx'];

  return (
    <FormField
      label={label}
      helpText={resolvedHelpText}
      errorText={resolvedErrorText}
      required={required}
    >
      {(props) => {
        const { label: _label, ...fieldProps } = props;
        return (
          <MuiSelect
            {...rest}
            {...fieldProps}
            fullWidth={fullWidth}
            error={error}
            required={required}
            size={size}
            label={undefined}
            notched={false}
            sx={mergedSx}
          />
        );
      }}
    </FormField>
  );
};
